import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import AISuggestionsBanner from "../components/AISuggestionsBanner";
import { persistAcceptedSuggestions, suggestionName } from "../utils/acceptSuggestions";
import StepDialog from "../components/StepDialog";
import AIPowerSearch from "../components/AIPowerSearch";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { 
  Shield, AlertTriangle, MapPin, Save, CheckCircle2, XCircle, 
  Building2, Server, Users, Package, Database, Laptop,
  Search, Filter, RefreshCw, Settings2, Eye
} from "lucide-react";
import { Cog } from "lucide-react";

const ASSET_TYPE_ICONS = {
  data_center: Server,
  vendor_supply_chain: Package,
  it_infrastructure: Laptop,
  information_assets: Database,
  work_area_facility: Building2,
  people_staff: Users,
  business_process: Cog
};

const CATEGORY_COLORS = {
  environmental: "bg-blue-100 text-blue-700 border-blue-200",
  utilities: "bg-amber-100 text-amber-700 border-amber-200",
  equipment: "bg-purple-100 text-purple-700 border-purple-200",
  deliberate: "bg-red-100 text-red-700 border-red-200",
  other: "bg-slate-100 text-slate-700 border-slate-200"
};

const CATEGORY_NAMES = {
  environmental: "Environmental",
  utilities: "Utilities",
  equipment: "Equipment",
  deliberate: "Deliberate",
  other: "Other"
};

export default function ThreatProfilingPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [stats, setStats] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [aiSearchResults, setAiSearchResults] = useState(null);
  const [modifiedProfiles, setModifiedProfiles] = useState({});

  // StepDialog state
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [editingThreat, setEditingThreat] = useState(null);
  const [wizardForm, setWizardForm] = useState({
    applicability: true,
    threat_rating: 2,
    vulnerability_occurrence: "medium",
    vulnerability_impact: "",
    affected_asset_types: []
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [profilesRes, assetTypesRes] = await Promise.all([
        api.get("/threat-profiles"),
        api.get("/threat-profiles/asset-types")
      ]);
      setProfiles(profilesRes.data.profiles || []);
      setStats(profilesRes.data.stats || {});
      setAssetTypes(assetTypesRes.data.asset_types || []);
    } catch (error) {
      toast.error("Failed to load threat profiles");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Accept AI threat suggestions → promote each into a NEW custom org threat via
  // POST /threat-profiles/promote-suggestion (mirrors _promote_threat's safe structure,
  // fresh UUID4, UNRATED). Dedupe against the current catalog names (library + custom).
  const handleAcceptThreatSuggestions = async (items) => {
    const result = await persistAcceptedSuggestions({
      items,
      existingKeys: (profiles || []).map((p) => p.name),
      keyOf: (raw) => suggestionName(raw),
      createOne: async (raw) => {
        const o = raw && typeof raw === "object" ? raw : {};
        const payload = { name: suggestionName(raw) };
        const desc = o.description || o.rationale || "";
        if (desc) payload.description = String(desc);
        if (o.category) payload.category = String(o.category);
        await api.post("/threat-profiles/promote-suggestion", payload);
      },
    });
    await fetchData();
    return result;
  };

  const openWizard = (threat) => {
    setEditingThreat(threat);
    setWizardForm({
      applicability: threat.applicability ?? true,
      threat_rating: threat.threat_rating ?? 2,
      vulnerability_occurrence: threat.vulnerability_occurrence || "medium",
      vulnerability_impact: threat.vulnerability_impact || "",
      affected_asset_types: threat.affected_asset_types || []
    });
    setWizardStep(0);
    setShowWizard(true);
  };

  const toggleAssetInWizard = (assetTypeId) => {
    setWizardForm(prev => {
      const current = prev.affected_asset_types || [];
      const next = current.includes(assetTypeId)
        ? current.filter(t => t !== assetTypeId)
        : [...current, assetTypeId];
      return { ...prev, affected_asset_types: next };
    });
  };

  const handleWizardSave = async () => {
    if (!editingThreat) return;
    try {
      setSaving(true);
      const profileData = {
        threat_id: editingThreat.id,
        applicability: wizardForm.applicability,
        threat_rating: wizardForm.threat_rating,
        vulnerability_occurrence: wizardForm.vulnerability_occurrence,
        vulnerability_impact: wizardForm.vulnerability_impact,
        affected_asset_types: wizardForm.affected_asset_types
      };
      const response = await api.post("/threat-profiles/save", profileData);
      toast.success(`Saved ${editingThreat.name} profile`);
      if (response.data.recalculated_assessments > 0) {
        toast.info(`Auto-recalculated ${response.data.recalculated_assessments} risk assessments`);
      }
      setShowWizard(false);
      setEditingThreat(null);
      setModifiedProfiles(prev => {
        const updated = { ...prev };
        delete updated[editingThreat.id];
        return updated;
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const saveAllModified = async () => {
    const modifiedThreatIds = Object.keys(modifiedProfiles);
    if (modifiedThreatIds.length === 0) {
      toast.info("No changes to save");
      return;
    }
    try {
      setSaving(true);
      const profilesToSave = modifiedThreatIds.map(threatId => {
        const threat = profiles.find(p => p.id === threatId);
        return {
          threat_id: threat.id,
          applicability: threat.applicability,
          threat_rating: threat.threat_rating,
          vulnerability_occurrence: threat.vulnerability_occurrence || "medium",
          vulnerability_impact: threat.vulnerability_impact || "",
          affected_asset_types: threat.affected_asset_types || []
        };
      });
      const response = await api.post("/threat-profiles/bulk-save", { profiles: profilesToSave });
      toast.success(`Saved ${response.data.saved_count} threat profiles`);
      if (response.data.recalculated_assessments > 0) {
        toast.info(`Auto-recalculated ${response.data.recalculated_assessments} risk assessments`);
      }
      setModifiedProfiles({});
      fetchData();
    } catch (error) {
      toast.error("Failed to save profiles");
    } finally {
      setSaving(false);
    }
  };

  const quickToggleApplicability = (threatId, checked) => {
    setProfiles(prev => prev.map(p => 
      p.id === threatId ? { ...p, applicability: checked } : p
    ));
    setModifiedProfiles(prev => ({
      ...prev,
      [threatId]: { ...(prev[threatId] || {}), applicability: checked }
    }));
  };

  // Filter profiles
  const filteredProfiles = (aiSearchResults || profiles).filter(p => {
    const matchesSearch = !searchTerm || p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          p.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === "all" || p.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Group by category
  const groupedProfiles = filteredProfiles.reduce((acc, profile) => {
    const cat = profile.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(profile);
    return acc;
  }, {});

  if (loading && profiles.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-sky-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="threat-profiling-page">
      <AISuggestionsBanner module="threat_profiling" onAccept={handleAcceptThreatSuggestions}
        existingKeys={(profiles || []).map((p) => p.name)}
        itemKeyOf={(raw) => suggestionName(raw)} />
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <MapPin className="w-8 h-8 text-sky-700" />
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Regional Threat Profiling</h1>
          </div>
          <p className="text-slate-600">
            Configure the 38-threat library based on your organization's geographic and operational context
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            onClick={saveAllModified} 
            disabled={saving || Object.keys(modifiedProfiles).length === 0}
            className="bg-sky-700 hover:bg-sky-800"
            data-testid="save-all-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            Save All Changes ({Object.keys(modifiedProfiles).length})
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Threats</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="stat-total">{stats.total_threats || 38}</p>
              </div>
              <Shield className="w-8 h-8 text-sky-700 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Profiled</p>
                <p className="text-2xl font-bold text-emerald-600" data-testid="stat-profiled">{stats.profiled_count || 0}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-emerald-500 opacity-50" />
            </div>
            <Progress value={stats.completion_percentage || 0} className="mt-2 h-2" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Applicable</p>
                <p className="text-2xl font-bold text-blue-600" data-testid="stat-applicable">{stats.applicable_count || 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">High Rating</p>
                <p className="text-2xl font-bold text-red-600" data-testid="stat-high-rating">{stats.high_rating_count || 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Banner */}
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Important: Locked Threat Ratings</p>
              <p className="text-sm text-amber-700">
                Threat ratings set here become the baseline for all risk assessments. Business Users cannot override these values.
                When you change a rating, all existing risk assessments will be automatically recalculated.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AI Power Search + Filters */}
      <AIPowerSearch
        module="threats"
        onResults={(results) => setAiSearchResults(results)}
        onClear={() => setAiSearchResults(null)}
        placeholder='Try: "find all cyber threats" or "show environmental hazards"...'
      />
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search threats..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="threat-search-input"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[200px]" data-testid="category-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="environmental">Environmental (13)</SelectItem>
            <SelectItem value="utilities">Utilities (4)</SelectItem>
            <SelectItem value="equipment">Equipment (5)</SelectItem>
            <SelectItem value="deliberate">Deliberate (6)</SelectItem>
            <SelectItem value="other">Other (10)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Threat Profiles by Category */}
      <Tabs defaultValue="all" className="space-y-4">
        <TabsList>
          <TabsTrigger value="all">All Threats ({filteredProfiles.length})</TabsTrigger>
          {Object.keys(groupedProfiles).map(cat => (
            <TabsTrigger key={cat} value={cat}>
              {CATEGORY_NAMES[cat]} ({groupedProfiles[cat].length})
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          {Object.entries(groupedProfiles).map(([category, threats]) => (
            <Card key={category}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <Badge className={CATEGORY_COLORS[category]}>{CATEGORY_NAMES[category]}</Badge>
                  <span className="text-sm font-normal text-slate-500">({threats.length} threats)</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {threats.map(threat => (
                  <ThreatRow 
                    key={threat.id}
                    threat={threat}
                    modified={!!modifiedProfiles[threat.id]}
                    onConfigure={() => openWizard(threat)}
                    onToggleApplicability={(checked) => quickToggleApplicability(threat.id, checked)}
                  />
                ))}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {Object.entries(groupedProfiles).map(([category, threats]) => (
          <TabsContent key={category} value={category} className="space-y-2">
            {threats.map(threat => (
              <ThreatRow 
                key={threat.id}
                threat={threat}
                modified={!!modifiedProfiles[threat.id]}
                onConfigure={() => openWizard(threat)}
                onToggleApplicability={(checked) => quickToggleApplicability(threat.id, checked)}
              />
            ))}
          </TabsContent>
        ))}
      </Tabs>

      {/* ── StepDialog Wizard for Threat Profile Configuration ── */}
      <StepDialog
        open={showWizard}
        onOpenChange={(open) => { setShowWizard(open); if (!open) { setWizardStep(0); setEditingThreat(null); } }}
        title={editingThreat ? `Configure: ${editingThreat.name}` : "Configure Threat"}
        currentStep={wizardStep}
        onStepChange={setWizardStep}
        onSubmit={handleWizardSave}
        onCancel={() => setShowWizard(false)}
        submitLabel={saving ? "Saving..." : "Save Profile"}
        submitDisabled={saving}
        saving={saving}
        maxWidth="max-w-2xl"
        canAdvance={true}
        steps={[
          { id: "applicability", title: "Applicability", subtitle: "Is this threat relevant to your organization? Set the baseline threat rating.", isComplete: true },
          { id: "vulnerability", title: "Vulnerability", subtitle: "How vulnerable is your region to this threat? Describe the specific regional impact.", isComplete: !!wizardForm.vulnerability_occurrence },
          { id: "assets", title: "Asset Mapping", subtitle: "Which asset/enabler categories can this threat affect? This filters threats for Business Users based on their BIA assets.", isComplete: wizardForm.affected_asset_types.length > 0 },
          { id: "review", title: "Review & Save", subtitle: "Review your threat profile configuration before saving.", isComplete: false },
        ]}
      >
        {/* Step 0: Applicability & Rating */}
        {wizardStep === 0 && (
          <div className="space-y-6" data-testid="step-applicability">
            <div className="space-y-3">
              <Label className="text-base font-semibold">Threat Applicability</Label>
              <div
                className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                  wizardForm.applicability ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  {wizardForm.applicability ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-slate-400" />
                  )}
                  <div>
                    <p className="font-medium text-sm">
                      {wizardForm.applicability ? "Applicable" : "Not Applicable"}
                    </p>
                    <p className="text-xs text-slate-500">
                      {wizardForm.applicability
                        ? "This threat will be included in risk assessments"
                        : "This threat will be excluded from risk assessments"}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={wizardForm.applicability}
                  onCheckedChange={(checked) => setWizardForm(prev => ({ ...prev, applicability: checked }))}
                  data-testid="applicability-switch"
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-base font-semibold">Threat Rating (Locked for Business Users)</Label>
              <div className="space-y-2">
                {[
                  { value: 1, label: "Low", desc: "Low probability and limited potential impact", color: "border-green-400 bg-green-50" },
                  { value: 2, label: "Medium", desc: "Moderate probability with notable potential impact", color: "border-amber-400 bg-amber-50" },
                  { value: 3, label: "High", desc: "High probability or severe potential impact", color: "border-red-400 bg-red-50" },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setWizardForm(prev => ({ ...prev, threat_rating: opt.value }))}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                      wizardForm.threat_rating === opt.value
                        ? `${opt.color} ring-1 ring-opacity-50`
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`rating-${opt.value}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                        opt.value === 3 ? 'bg-red-500' : opt.value === 2 ? 'bg-amber-500' : 'bg-green-500'
                      }`}>
                        {opt.value}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{opt.label}</p>
                        <p className="text-xs text-slate-500">{opt.desc}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 1: Vulnerability Assessment */}
        {wizardStep === 1 && (
          <div className="space-y-5" data-testid="step-vulnerability">
            <div className="space-y-3">
              <Label className="text-base font-semibold">Regional Vulnerability Occurrence</Label>
              <div className="space-y-2">
                {[
                  { value: "low", label: "Low", desc: "Rarely occurs in this geographic region" },
                  { value: "medium", label: "Medium", desc: "Occasional occurrence in this region" },
                  { value: "high", label: "High", desc: "Frequent or recurring occurrence in this region" },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setWizardForm(prev => ({ ...prev, vulnerability_occurrence: opt.value }))}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                      wizardForm.vulnerability_occurrence === opt.value
                        ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-200'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`occurrence-${opt.value}`}
                  >
                    <p className="font-medium text-sm">{opt.label}</p>
                    <p className="text-xs text-slate-500">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-base font-semibold">Regional Vulnerability Impact</Label>
              <Textarea
                placeholder="Describe the specific regional impact of this threat (e.g., 'Coastal flooding can disable our primary data center within 2 hours')"
                value={wizardForm.vulnerability_impact}
                onChange={(e) => setWizardForm(prev => ({ ...prev, vulnerability_impact: e.target.value }))}
                rows={3}
                data-testid="vulnerability-impact-input"
              />
            </div>
          </div>
        )}

        {/* Step 2: Asset Mapping */}
        {wizardStep === 2 && (
          <div className="space-y-4" data-testid="step-assets">
            <Label className="text-base font-semibold">Affected Asset/Enabler Types</Label>
            <p className="text-sm text-slate-500">
              Select which asset categories this threat can affect. This determines which Business Users see this threat during their BIA-driven risk assessments.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {assetTypes.map(assetType => {
                const Icon = ASSET_TYPE_ICONS[assetType.id] || Building2;
                const isSelected = (wizardForm.affected_asset_types || []).includes(assetType.id);
                return (
                  <button
                    key={assetType.id}
                    type="button"
                    onClick={() => toggleAssetInWizard(assetType.id)}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 text-left transition-all ${
                      isSelected 
                        ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-200' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`asset-type-${assetType.id}`}
                  >
                    <Icon className={`w-5 h-5 ${isSelected ? 'text-sky-700' : 'text-slate-400'}`} />
                    <span className="text-sm font-medium">{assetType.name}</span>
                    {isSelected && <CheckCircle2 className="w-4 h-4 ml-auto text-sky-600" />}
                  </button>
                );
              })}
            </div>
            {wizardForm.affected_asset_types.length === 0 && (
              <p className="text-xs text-amber-600">No asset types selected. Select at least one for this threat to appear in BIA-driven assessments.</p>
            )}
          </div>
        )}

        {/* Step 3: Review */}
        {wizardStep === 3 && editingThreat && (
          <div className="space-y-4" data-testid="step-review">
            <div className="p-4 bg-slate-50 rounded-lg space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Threat</span>
                <span className="font-medium">{editingThreat.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Category</span>
                <Badge className={CATEGORY_COLORS[editingThreat.category]}>{CATEGORY_NAMES[editingThreat.category]}</Badge>
              </div>
              <div className="flex justify-between text-sm items-center">
                <span className="text-slate-500">Applicable</span>
                <Badge className={wizardForm.applicability ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
                  {wizardForm.applicability ? "Yes" : "No"}
                </Badge>
              </div>
              <div className="flex justify-between text-sm items-center">
                <span className="text-slate-500">Threat Rating</span>
                <Badge className={
                  wizardForm.threat_rating === 3 ? 'bg-red-100 text-red-700' :
                  wizardForm.threat_rating === 2 ? 'bg-amber-100 text-amber-700' :
                  'bg-green-100 text-green-700'
                }>
                  {wizardForm.threat_rating === 3 ? 'High (3)' : wizardForm.threat_rating === 2 ? 'Medium (2)' : 'Low (1)'}
                </Badge>
              </div>
              <div className="flex justify-between text-sm items-center">
                <span className="text-slate-500">Vulnerability Occurrence</span>
                <span className="font-medium capitalize">{wizardForm.vulnerability_occurrence}</span>
              </div>
              {wizardForm.vulnerability_impact && (
                <div className="text-sm">
                  <span className="text-slate-500 block mb-1">Regional Impact</span>
                  <p className="text-slate-700 bg-white p-2 rounded border text-xs">{wizardForm.vulnerability_impact}</p>
                </div>
              )}
              <div className="text-sm">
                <span className="text-slate-500 block mb-1">Affected Asset Types ({wizardForm.affected_asset_types.length})</span>
                <div className="flex flex-wrap gap-1.5">
                  {wizardForm.affected_asset_types.length > 0 ? (
                    wizardForm.affected_asset_types.map(id => {
                      const at = assetTypes.find(a => a.id === id);
                      return (
                        <Badge key={id} variant="outline" className="text-xs bg-sky-50">
                          {at?.name || id}
                        </Badge>
                      );
                    })
                  ) : (
                    <span className="text-xs text-amber-600">None selected</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </StepDialog>
    </div>
  );
}

function ThreatRow({ threat, modified, onConfigure, onToggleApplicability }) {
  return (
    <div 
      className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
        modified ? 'border-amber-400 bg-amber-50/50' : 'border-slate-200 hover:border-slate-300'
      } ${!threat.applicability ? 'opacity-60' : ''}`}
      data-testid={`threat-card-${threat.id}`}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <Switch
          checked={threat.applicability}
          onCheckedChange={onToggleApplicability}
          data-testid={`toggle-${threat.id}`}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-slate-900 text-sm">{threat.name}</span>
            <Badge variant="outline" className="text-xs">{threat.id}</Badge>
            {threat.profiled && <Badge className="bg-emerald-100 text-emerald-700 text-xs">Profiled</Badge>}
            {modified && <Badge className="bg-amber-100 text-amber-700 text-xs">Modified</Badge>}
          </div>
          <p className="text-xs text-slate-500 truncate">{threat.description}</p>
        </div>
      </div>
      <div className="flex items-center gap-3 ml-3">
        {threat.unrated ? (
          <Badge className="bg-slate-100 text-slate-600" data-testid={`rating-badge-${threat.id}`}>Unrated</Badge>
        ) : (
          <Badge className={
            threat.threat_rating === 3 ? 'bg-red-100 text-red-700' :
            threat.threat_rating === 2 ? 'bg-amber-100 text-amber-700' :
            'bg-green-100 text-green-700'
          } data-testid={`rating-badge-${threat.id}`}>
            {threat.threat_rating === 3 ? 'High' : threat.threat_rating === 2 ? 'Medium' : 'Low'}
          </Badge>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={onConfigure}
          className="shrink-0"
          data-testid={`configure-${threat.id}`}
        >
          <Settings2 className="w-3.5 h-3.5 mr-1.5" />
          Configure
        </Button>
      </div>
    </div>
  );
}
